import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function InvoiceView() {
     const { token } = useParams();
     const [searchParams] = useSearchParams();
     const [invoice, setInvoice] = useState(null);
     const [loading, setLoading] = useState(true);
     const justPaid = searchParams.get('paid') === 'true';

     useEffect(() => {
          loadInvoice();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [token]);

     async function loadInvoice() {
          const { data } = await supabase
               .from('invoices')
               .select('*')
               .eq('token', token)
               .single();

          if (data) {
               setInvoice(data);
               if (data.status === 'sent') {
                    await supabase
                         .from('invoices')
                         .update({ status: 'viewed', updated_at: new Date().toISOString() })
                         .eq('id', data.id);
               }
          }
          setLoading(false);
     }

     if (loading) {
          return (
               <div className='min-h-screen bg-black flex items-center justify-center'>
                    <p className='text-white/60'>Loading invoice...</p>
               </div>
          );
     }

     if (!invoice) {
          return (
               <div className='min-h-screen bg-black flex items-center justify-center'>
                    <p className='text-white/60'>Invoice not found.</p>
               </div>
          );
     }

     const lineItems = invoice.line_items || [];
     const isPaid = invoice.status === 'paid' || justPaid;

     return (
          <div className='min-h-screen bg-black text-white'>
               <div className='max-w-2xl mx-auto px-4 py-12'>
                    {/* Header */}
                    <div className='text-center mb-8'>
                         <h1 className='text-yellow text-2xl font-bold'>Ripout Rentals</h1>
                         <p className='text-white/40 text-sm mt-1'>Invoice {invoice.invoice_number}</p>
                    </div>

                    {/* Paid Banner */}
                    {isPaid && (
                         <div className='bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center mb-6'>
                              <h2 className='text-green-300 text-xl font-bold'>
                                   {justPaid ? 'Payment Received — Thank You!' : 'Invoice Paid'}
                              </h2>
                              {invoice.paid_at && (
                                   <p className='text-white/40 text-sm mt-2'>
                                        Paid on {new Date(invoice.paid_at).toLocaleString()}
                                   </p>
                              )}
                         </div>
                    )}

                    {/* Invoice Details */}
                    <div className='bg-darker rounded-xl border border-dark p-6 mb-6'>
                         <div className='flex justify-between mb-6'>
                              <div>
                                   <p className='text-white/40 text-sm'>Bill To</p>
                                   <p className='text-white font-bold'>{invoice.customer_name}</p>
                                   <p className='text-white/60 text-sm'>{invoice.customer_email}</p>
                              </div>
                              {invoice.due_date && (
                                   <div className='text-right'>
                                        <p className='text-white/40 text-sm'>Due Date</p>
                                        <p className='text-white'>{invoice.due_date}</p>
                                   </div>
                              )}
                         </div>

                         <table className='w-full text-sm mb-6'>
                              <thead>
                                   <tr className='border-b border-dark text-white/40'>
                                        <th className='text-left py-2'>Description</th>
                                        <th className='text-right py-2'>Qty</th>
                                        <th className='text-right py-2'>Price</th>
                                        <th className='text-right py-2'>Total</th>
                                   </tr>
                              </thead>
                              <tbody className='divide-y divide-dark'>
                                   {lineItems.map((li, i) => (
                                        <tr key={i}>
                                             <td className='py-3 text-white'>{li.description}</td>
                                             <td className='py-3 text-white/60 text-right'>{li.quantity}</td>
                                             <td className='py-3 text-white/60 text-right'>
                                                  ${(li.unit_price_cents / 100).toFixed(2)}
                                             </td>
                                             <td className='py-3 text-white text-right'>
                                                  ${(li.total_cents / 100).toLocaleString()}
                                             </td>
                                        </tr>
                                   ))}
                              </tbody>
                         </table>

                         <div className='border-t border-dark pt-4 space-y-2 text-sm'>
                              <div className='flex justify-between text-white/60'>
                                   <span>Subtotal</span>
                                   <span>${(invoice.subtotal_cents / 100).toLocaleString()}</span>
                              </div>
                              {invoice.tax_cents > 0 && (
                                   <div className='flex justify-between text-white/60'>
                                        <span>Tax</span>
                                        <span>${(invoice.tax_cents / 100).toLocaleString()}</span>
                                   </div>
                              )}
                              <div className='flex justify-between text-white font-bold text-lg pt-2 border-t border-dark'>
                                   <span>Total Due</span>
                                   <span className='text-yellow'>
                                        ${(invoice.total_cents / 100).toLocaleString()}
                                   </span>
                              </div>
                         </div>

                         {invoice.notes && (
                              <div className='mt-6 pt-4 border-t border-dark'>
                                   <p className='text-white/40 text-sm mb-1'>Notes</p>
                                   <p className='text-white/80 text-sm whitespace-pre-wrap'>{invoice.notes}</p>
                              </div>
                         )}
                    </div>

                    {/* Payment Options */}
                    {!isPaid && (
                         <div className='space-y-4'>
                              {/* Stripe */}
                              {invoice.stripe_payment_link && (
                                   <a
                                        href={invoice.stripe_payment_link}
                                        className='block w-full bg-yellow text-black text-center font-bold py-4 rounded-xl hover:bg-yellow/90 duration-300'
                                   >
                                        Pay with Credit / Debit Card
                                   </a>
                              )}

                              {/* Zelle */}
                              <div className='bg-darker rounded-xl border border-dark p-6 text-center'>
                                   <h3 className='text-white font-bold mb-3'>Pay with Zelle</h3>
                                   <div className='w-48 h-48 bg-dark rounded-xl border border-dark mx-auto flex items-center justify-center mb-3'>
                                        <p className='text-white/30 text-sm'>Zelle QR Placeholder</p>
                                   </div>
                                   <p className='text-white/60 text-sm'>
                                        Scan the QR code above or send payment via Zelle to{' '}
                                        <span className='text-yellow'>sales@ripoutrentals.com</span>
                                   </p>
                                   <p className='text-white/40 text-xs mt-2'>
                                        Reference: {invoice.invoice_number}
                                   </p>
                              </div>
                         </div>
                    )}

                    {/* Footer */}
                    <div className='text-center mt-8'>
                         <a href='/' className='text-white/30 text-sm hover:text-white/60 duration-300'>
                              Ripout Rentals&trade; | Phoenix, Arizona
                         </a>
                    </div>
               </div>
          </div>
     );
}
